import { type ExcludeUndefined } from "@validereinc/utilities";
import { isEmpty } from "es-toolkit/compat";
import type {
  DeviceCreateOneRequestType,
  DeviceGetListRequestType,
  DeviceType,
  DeviceTypeType,
} from "../schemas/DeviceSchemas.ts";
import { DeviceGetListRequestSchema } from "../schemas/DeviceSchemas.ts";
import type { ReportWithDownloadV2Type } from "../schemas/ReportSchemas.ts";
import type {
  CreateOneResponseType,
  GetListResponseType,
  GetOneRequestType,
  ResourceAdapterToAdapterQueryKeysType,
  ResourceAdapterType,
} from "../util/index.ts";
import {
  Resources,
  SortDirection,
  fetchAndCollate,
  getFilterObject,
} from "../util/index.ts";
import { restAPI } from "./api.ts";
import {
  SavedFiltersAdapter,
  type SavedFiltersAdapterType,
} from "./composable/index.ts";

export interface DeviceAdapterType
  extends Pick<
    ResourceAdapterType<DeviceType>,
    "getList" | "getOne" | "updateOne" | "createOne" | "deleteOne"
  > {
  getList: (
    params: DeviceGetListRequestType
  ) => Promise<GetListResponseType<DeviceType>>;
  exportList: (
    params: DeviceGetListRequestType
  ) => Promise<ReportWithDownloadV2Type>;
  createOne: (
    params: DeviceCreateOneRequestType
  ) => Promise<CreateOneResponseType<DeviceType>>;
  savedFilters: SavedFiltersAdapterType<
    typeof DeviceGetListRequestSchema.shape.filters
  >;
  types: {
    getList: () => Promise<GetListResponseType<DeviceTypeType>>;
  };
}

const DevicesTypesAdapter = {
  getList: () =>
    restAPI.nodeAPI
      .GET<DeviceTypeType[]>({ endpoint: "/device_types" })
      .then((data) => ({
        page_number: 1,
        total_pages: 1,
        page_size: data.length,
        total_entries: data.length,
        data,
      })),
};

export const DeviceAdapter: DeviceAdapterType = {
  exportList: ({
    filters,
    sortBy = "name",
    sortDirection = "asc",
    meta = {},
  }) => {
    const { status, ...restFilters } = filters ?? {};

    return restAPI.nodeAPI.POST<ReportWithDownloadV2Type>({
      endpoint: "/devices/export",
      query: meta.period ? { period: meta.period } : undefined,
      body: {
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: {
          ...getFilterObject(restFilters),
          ...(typeof status === "string" && !isEmpty(status)
            ? { status }
            : getFilterObject<
                ExcludeUndefined<DeviceGetListRequestType["filters"]>
              >({ status })),
        },
      },
    });
  },

  getList: ({
    filters,
    pageSize,
    page,
    sortBy = "name",
    sortDirection = SortDirection.ASCENDING,
    groupBy,
    meta = {},
  }) => {
    const { status, ...restFilters } = filters ?? {};

    return fetchAndCollate(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.POST<GetListResponseType<DeviceType>>({
          endpoint: "/devices/search",
          query: meta.period ? { period: meta.period } : undefined,
          body: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            group_by: groupBy,
            filter: {
              ...getFilterObject(restFilters),
              ...(typeof status === "string" && !isEmpty(status)
                ? { status }
                : getFilterObject<
                    ExcludeUndefined<DeviceGetListRequestType["filters"]>
                  >({ status })),
            },
          },
        }),
      page,
      pageSize
    );
  },

  getOne: ({ id, meta }: GetOneRequestType<{ period?: string }>) =>
    restAPI.nodeAPI
      .GET<DeviceType>({
        endpoint: `/devices/${id}`,
        query: meta?.period ? { period: meta.period } : {},
      })
      .then((resp) => ({
        data: resp,
      })),

  updateOne: ({ id, data }) =>
    restAPI.nodeAPI
      .PUT<DeviceType>({
        endpoint: `/devices/${id}`,
        body: data,
      })
      .then((resp) => ({
        data: resp,
      })),

  createOne: (props) =>
    restAPI.nodeAPI
      .POST<DeviceType>({
        endpoint: "/devices",
        body: props.data,
      })
      .then((resp) => ({ data: resp })),

  deleteOne: ({ id }: { id: string }) =>
    restAPI.nodeAPI.DELETE({ endpoint: `/devices/${id}` }),

  savedFilters: SavedFiltersAdapter({
    endpoint: "/devices/filters",
  }),

  types: DevicesTypesAdapter,
};

export const DeviceAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<DeviceAdapterType> =
  {
    getList: (params) => [Resources.DEVICE, "getList", params] as const,
    exportList: (params) => [Resources.DEVICE, "export", params] as const,
    getOne: (params) => [Resources.DEVICE, "getOne", params.id] as const,
    updateOne: (_) => [Resources.DEVICE] as const,
    createOne: (_) => [Resources.DEVICE] as const,
    deleteOne: (_) => [Resources.DEVICE] as const,
    types: {
      getList: () => [Resources.DEVICE, "types", "getList"] as const,
    },
    savedFilters: {
      getList: (params) =>
        [Resources.DEVICE, "filters", "getList", params] as const,
      updateOne: (_) => [Resources.DEVICE, "filters"] as const,
      createOne: (_) => [Resources.DEVICE, "filters"] as const,
      deleteOne: (_) => [Resources.DEVICE, "filters"] as const,
    },
  };
